import { useState, useMemo, useEffect } from 'react';
import { useListStagedSessionPositionsForPAndS, useActionOnStagedSessionPositionsForPAndS, BrokerageAccountPositionStaging, UNKNOWN_TRADER_ID } from '@/lib/api/risk/brokerage';
import { useListBaseTraders } from '@/lib/api/trader';
import { SelectOptionItem } from '@/lib/models/ui';
import { PositionCard } from './PositionCard';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { Loader, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';

export function RiskBrokeragePAndSTab() {
    const { toast } = useToast();
    const [selectedTraderId, setSelectedTraderId] = useState<string | null>(null);
    const [filters, setFilters] = useState<{ customerName: string; tradingAccount: string }>({
        customerName: "",
        tradingAccount: ""
    });
    const clearFilters = () => setFilters({ customerName: "", tradingAccount: "" });
    const [selectedAcceptHedgeable, setSelectedAcceptHedgeable] = useState<Set<string>>(new Set());
    const [selectedAcceptNonHedgeable, setSelectedAcceptNonHedgeable] = useState<Set<string>>(new Set());
    const [selectedDismiss, setSelectedDismiss] = useState<Set<string>>(new Set());
    const [collapsedSections, setCollapsedSections] = useState({
        acceptHedgeable: false,
        acceptNonHedgeable: false,
        flagged: false,
        nonApplicable: false
    });
    const [isApplyingChanges, setIsApplyingChanges] = useState(false);

    const diffResult = useListStagedSessionPositionsForPAndS();
    const tradersResult = useListBaseTraders();
    const actionOnStaged = useActionOnStagedSessionPositionsForPAndS();

    const traders_list = tradersResult?.data?.data;
    const diff_data = diffResult?.data?.data;

    // Set default trader to current user on mount
    useEffect(() => {
        if (!selectedTraderId && traders_list?.length) {
            const currentTrader = traders_list.find((t: any) => t.is_current_user);
            if (currentTrader) {
                setSelectedTraderId(currentTrader.id);
            }
        }
    }, [traders_list, selectedTraderId]);

    // Auto-mark positions on load: suggested_accept → accepted, others → dismissed
    useEffect(() => {
        if (!diff_data?.suggested_report || !selectedTraderId) return;

        const traderData = diff_data.suggested_report[selectedTraderId];
        if (!traderData) return;

        const acceptHedgeableIds = new Set<string>();
        const acceptNonHedgeableIds = new Set<string>();
        const dismissIds = new Set<string>();

        // Hedgeable positions
        traderData.suggested_accept_hedgeable?.forEach((item: BrokerageAccountPositionStaging) => acceptHedgeableIds.add(item.id));

        // Non-hedgeable positions
        traderData.suggested_accept_non_hedgeable?.forEach((item: BrokerageAccountPositionStaging) => acceptNonHedgeableIds.add(item.id));

        // Dismissed positions - flagged and non_applicable should be dismissed by default
        [...(traderData.suggested_flagged || []), ...(traderData.suggested_non_applicable || [])]
            .forEach((item: BrokerageAccountPositionStaging) => dismissIds.add(item.id));

        setSelectedAcceptHedgeable(acceptHedgeableIds);
        setSelectedAcceptNonHedgeable(acceptNonHedgeableIds);
        setSelectedDismiss(dismissIds);
    }, [diff_data, selectedTraderId]);

    const isLoading = diffResult?.isLoading;
    const isRefreshing = diffResult?.isFetching;
    const encounteredError = !isLoading && (!!diffResult?.error || !diffResult?.data?.success);

    // Get trader options for combobox, including "Unknown" if present
    const traderOptions: SelectOptionItem<string>[] = useMemo(() => {
        const options: SelectOptionItem<string>[] = [];

        if (traders_list) {
            options.push(...traders_list.map((trader: { id: string; name: string }) => ({
                value: trader.id,
                label: trader.name
            })));
        }

        // Add "Unknown" if it exists in the response
        if (diff_data?.suggested_report && diff_data.suggested_report[UNKNOWN_TRADER_ID]) {
            options.push({
                value: UNKNOWN_TRADER_ID,
                label: 'Unknown'
            });
        }

        return options.sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label));
    }, [traders_list, diff_data]);

    // Get staged data for selected trader
    const selectedTraderStaged = useMemo(() => {
        if (!selectedTraderId || !diff_data?.suggested_report) return null;
        return diff_data.suggested_report[selectedTraderId];
    }, [selectedTraderId, diff_data]);

    // Calculate totals for all traders
    const totals = useMemo(() => {
        if (!diff_data?.suggested_report) return {
            totalAcceptedHedgeable: 0,
            totalAcceptedNonHedgeable: 0,
            totalFlagged: 0,
            totalNonApplicable: 0,
            fullTotal: 0
        };

        let totalAcceptedHedgeable = 0;
        let totalAcceptedNonHedgeable = 0;
        let totalFlagged = 0;
        let totalNonApplicable = 0;

        Object.values(diff_data.suggested_report).forEach((traderData: any) => {
            totalAcceptedHedgeable += traderData.suggested_accept_hedgeable?.length || 0;
            totalAcceptedNonHedgeable += traderData.suggested_accept_non_hedgeable?.length || 0;
            totalFlagged += traderData.suggested_flagged?.length || 0;
            totalNonApplicable += traderData.suggested_non_applicable?.length || 0;
        });

        return {
            totalAcceptedHedgeable,
            totalAcceptedNonHedgeable,
            totalFlagged,
            totalNonApplicable,
            fullTotal: totalAcceptedHedgeable + totalAcceptedNonHedgeable + totalFlagged + totalNonApplicable
        };
    }, [diff_data]);

    // Client-side filter: substring, case-insensitive match against customer label
    // (Customer.long_name with fallback to customer_name, mirroring PositionCard display)
    // and trading_account. Does NOT affect selections, totals, auto-mark, or Apply Changes.
    const matchesFilters = (item: BrokerageAccountPositionStaging) => {
        const d = item.brokerage_account_data;
        const customerLabel = (d.Customer?.long_name ?? d.customer_name ?? "").toLowerCase();
        const account = (d.trading_account ?? "").toLowerCase();

        const cnQuery = filters.customerName.trim().toLowerCase();
        const taQuery = filters.tradingAccount.trim().toLowerCase();

        if (cnQuery && !customerLabel.includes(cnQuery)) return false;
        if (taQuery && !account.includes(taQuery)) return false;
        return true;
    };

    // Sort staging positions by commodity and option month
    const sortStagingPositions = (positions: BrokerageAccountPositionStaging[]) => {
        return [...positions].sort((a, b) => {
            const aData = a.brokerage_account_data;
            const bData = b.brokerage_account_data;

            // Sort by commodity_label first
            const commodityCompare = (aData.commodity_label || '').localeCompare(bData.commodity_label || '');
            if (commodityCompare !== 0) return commodityCompare;

            // Then by option_month
            return (aData.option_month || '').localeCompare(bData.option_month || '');
        });
    };

    // Direct selection logic for three-way selection
    const handleSelectHedgeable = (id: string) => {
        setSelectedAcceptHedgeable(prev => new Set(prev).add(id));
        setSelectedAcceptNonHedgeable(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
        setSelectedDismiss(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
    };

    const handleSelectNonHedgeable = (id: string) => {
        setSelectedAcceptNonHedgeable(prev => new Set(prev).add(id));
        setSelectedAcceptHedgeable(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
        setSelectedDismiss(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
    };

    const handleSelectDismiss = (id: string) => {
        setSelectedDismiss(prev => new Set(prev).add(id));
        setSelectedAcceptHedgeable(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
        setSelectedAcceptNonHedgeable(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
    };

    // Check if all positions for selected trader are marked
    const allPositionsMarked = useMemo(() => {
        if (!selectedTraderId || !selectedTraderStaged) return false;

        const allStagingIds = new Set<string>();
        selectedTraderStaged.suggested_accept_hedgeable?.forEach((item: BrokerageAccountPositionStaging) => allStagingIds.add(item.id));
        selectedTraderStaged.suggested_accept_non_hedgeable?.forEach((item: BrokerageAccountPositionStaging) => allStagingIds.add(item.id));
        selectedTraderStaged.suggested_flagged?.forEach((item: BrokerageAccountPositionStaging) => allStagingIds.add(item.id));
        selectedTraderStaged.suggested_non_applicable?.forEach((item: BrokerageAccountPositionStaging) => allStagingIds.add(item.id));

        const markedIds = new Set([...selectedAcceptHedgeable, ...selectedAcceptNonHedgeable, ...selectedDismiss]);

        return Array.from(allStagingIds).every(id => markedIds.has(id));
    }, [selectedTraderId, selectedTraderStaged, selectedAcceptHedgeable, selectedAcceptNonHedgeable, selectedDismiss]);

    const handleApplyChanges = async () => {
        if (isApplyingChanges || !selectedTraderId) return;

        if (!allPositionsMarked) {
            toast({
                title: "Not all positions marked",
                description: "Please mark all positions as accept or dismiss before submitting.",
                variant: "destructive"
            });
            return;
        }

        setIsApplyingChanges(true);

        const acceptHedgeableIds = Array.from(selectedAcceptHedgeable);
        const acceptNonHedgeableIds = Array.from(selectedAcceptNonHedgeable);
        const dismissIds = Array.from(selectedDismiss);

        try {
            if (selectedTraderId === UNKNOWN_TRADER_ID) {
                toast({
                    title: "Cannot submit for unknown trader",
                    description: "Please assign positions to a specific trader before submitting.",
                    variant: "destructive"
                });
                setIsApplyingChanges(false);
                return;
            }

            const result = await actionOnStaged({
                trader_id: selectedTraderId,
                provider_type: "RJO",
                trading_order_type: "p_and_s",
                accepted_hedgeable_row_ids: acceptHedgeableIds,
                accepted_non_hedgeable_row_ids: acceptNonHedgeableIds,
                dismissed_row_ids: dismissIds
            });

            if (!result.success) {
                throw result;
            }

            toast({ title: "Changes applied successfully" });
            // Clear selections will happen via useEffect when data refetches
        } catch (error) {
            console.error("Failed to apply changes", error);
            toast({
                title: "Failed to apply changes",
                description: "An unexpected error occurred",
                variant: "destructive"
            });
        } finally {
            setIsApplyingChanges(false);
        }
    };

    const getSelectionState = (id: string) => {
        if (selectedAcceptHedgeable.has(id)) return 'hedgeable';
        if (selectedAcceptNonHedgeable.has(id)) return 'non_hedgeable';
        if (selectedDismiss.has(id)) return 'dismiss';
        return null;
    };

    const clearSelections = () => {
        setSelectedAcceptHedgeable(new Set());
        setSelectedAcceptNonHedgeable(new Set());
        setSelectedDismiss(new Set());
    };

    const acceptHedgeableCount = selectedAcceptHedgeable.size;
    const acceptNonHedgeableCount = selectedAcceptNonHedgeable.size;
    const dismissCount = selectedDismiss.size;

    if (encounteredError) {
        return (
            <div className="flex flex-col gap-4 h-full">
                <p className="text-red-500">Error loading data</p>
                <Button variant="outline" onClick={() => {
                    diffResult.refetch();
                }}>
                    Reload
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4 h-full">
            {/* Header with Trader Filter */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="w-full sm:min-w-[300px] flex flex-col md:flex-row gap-2 justify-between">
                    <div className="flex items-center px-2 h-10 min-h-10 max-h-10 bg-muted rounded-md text-xs gap-2 whitespace-nowrap">
                        <span className="text-muted-foreground">Staged Totals:</span>
                        <span
                            className="text-green-600 font-semibold"
                            title="Total Hedgeable Positions"
                        >
                            {totals.totalAcceptedHedgeable}
                        </span>
                        <span className="mx-1 opacity-50">|</span>
                        <span
                            className="text-blue-600 font-semibold"
                            title="Total Non-Hedgeable Positions"
                        >
                            {totals.totalAcceptedNonHedgeable}
                        </span>
                        <span className="mx-1 opacity-50">|</span>
                        <span
                            className="text-yellow-600 font-semibold"
                            title="Total Flagged"
                        >
                            {totals.totalFlagged}
                        </span>
                        <span className="mx-1 opacity-50">|</span>
                        <span
                            className="text-orange-600 font-semibold"
                            title="Total Non-Applicable"
                        >
                            {totals.totalNonApplicable}
                        </span>
                        <span className="mx-1 opacity-50">|</span>
                        <span
                            className="text-slate-600 font-semibold"
                            title="Full Total"
                        >
                            {totals.fullTotal}
                        </span>
                    </div>
                    <Combobox
                        className="min-w-[150px]"
                        popoverClassName="min-w-[150px]"
                        options={traderOptions}
                        value={selectedTraderId ?? ""}
                        onValueChange={(value) => {
                            clearSelections();
                            clearFilters();
                            setSelectedTraderId(value || null);
                        }}
                        placeholder="Filter by trader"
                    />
                    <Input
                        placeholder="Filter by customer name"
                        value={filters.customerName}
                        onChange={(e) => setFilters(prev => ({ ...prev, customerName: e.target.value }))}
                        className="min-w-[200px]"
                    />
                    <Input
                        placeholder="Filter by account #"
                        value={filters.tradingAccount}
                        onChange={(e) => setFilters(prev => ({ ...prev, tradingAccount: e.target.value }))}
                        className="min-w-[160px]"
                    />
                    <Button
                        variant="outline"
                        onClick={clearFilters}
                        disabled={!filters.customerName && !filters.tradingAccount}
                    >
                        Clear filters
                    </Button>
                </div>
            </div>

            {/* Action Bar */}
            {selectedTraderId && (
                <div className="sticky bottom-0 bg-background border-t p-4 shadow-lg z-10">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm">
                            <span className="text-green-600 font-medium">{acceptHedgeableCount} hedgeable</span>
                            <span className="text-blue-600 font-medium">{acceptNonHedgeableCount} non-hedgeable</span>
                            <span className="text-red-600 font-medium">{dismissCount} dismissed</span>
                            {!allPositionsMarked && (
                                <span className="text-yellow-600 font-medium">Not all positions marked</span>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button
                                onClick={handleApplyChanges}
                                disabled={!allPositionsMarked || selectedTraderId === UNKNOWN_TRADER_ID || isApplyingChanges}
                            >
                                {isApplyingChanges ? "Applying..." : "Apply Changes"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content - Single Column */}
            <div className="flex flex-col gap-4 flex-1 min-h-0">
                <Card className="flex flex-col">
                    <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                            <span>Staged P&S Positions</span>
                            {isRefreshing && <Loader className="h-4 w-4 animate-spin" />}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="overflow-y-auto flex flex-col gap-2 h-[70vh]">
                        {isLoading ? (
                            <div className="space-y-2">
                                {[...Array(5)].map((_, i) => (
                                    <Skeleton key={i} className="h-24" />
                                ))}
                            </div>
                        ) : !selectedTraderStaged ? (
                            <div className="text-center text-muted-foreground py-8">
                                {selectedTraderId ? "No staged P&S positions for this trader" : "Select a trader to view staged P&S positions"}
                            </div>
                        ) : (
                            <>
                                {/* Suggested Accept Hedgeable */}
                                {(selectedTraderStaged.suggested_accept_hedgeable?.length || 0) > 0 && (
                                    <div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="w-full flex items-center justify-between"
                                            onClick={() => setCollapsedSections(prev => ({ ...prev, acceptHedgeable: !prev.acceptHedgeable }))}
                                        >
                                            <h4 className="font-semibold text-sm text-green-600">Suggested Accept Hedgeable ({selectedTraderStaged.suggested_accept_hedgeable?.length || 0})</h4>
                                            {collapsedSections.acceptHedgeable ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                                        </Button>
                                        {!collapsedSections.acceptHedgeable && (
                                            <div className="space-y-2 mt-2">
                                                {sortStagingPositions(selectedTraderStaged.suggested_accept_hedgeable || []).filter(matchesFilters).map(staging => (
                                                    <PositionCard
                                                        key={staging.id}
                                                        position={staging.brokerage_account_data}
                                                        importDate={staging.import_date}
                                                        isStaged
                                                        suggestionType="add"
                                                        selectionState={getSelectionState(staging.id)}
                                                        onSelectHedgeable={() => handleSelectHedgeable(staging.id)}
                                                        onSelectNonHedgeable={() => handleSelectNonHedgeable(staging.id)}
                                                        onSelectDismiss={() => handleSelectDismiss(staging.id)}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Suggested Accept Non-Hedgeable */}
                                {(selectedTraderStaged.suggested_accept_non_hedgeable?.length || 0) > 0 && (
                                    <div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="w-full flex items-center justify-between"
                                            onClick={() => setCollapsedSections(prev => ({ ...prev, acceptNonHedgeable: !prev.acceptNonHedgeable }))}
                                        >
                                            <h4 className="font-semibold text-sm text-blue-600">Suggested Accept Non-Hedgeable ({selectedTraderStaged.suggested_accept_non_hedgeable?.length || 0})</h4>
                                            {collapsedSections.acceptNonHedgeable ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                                        </Button>
                                        {!collapsedSections.acceptNonHedgeable && (
                                            <div className="space-y-2 mt-2">
                                                {sortStagingPositions(selectedTraderStaged.suggested_accept_non_hedgeable || []).filter(matchesFilters).map(staging => (
                                                    <PositionCard
                                                        key={staging.id}
                                                        position={staging.brokerage_account_data}
                                                        isStaged
                                                        suggestionType="add"
                                                        selectionState={getSelectionState(staging.id)}
                                                        onSelectHedgeable={() => handleSelectHedgeable(staging.id)}
                                                        onSelectNonHedgeable={() => handleSelectNonHedgeable(staging.id)}
                                                        onSelectDismiss={() => handleSelectDismiss(staging.id)}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Suggested Flagged */}
                                {(selectedTraderStaged.suggested_flagged?.length || 0) > 0 && (
                                    <div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="w-full flex items-center justify-between"
                                            onClick={() => setCollapsedSections(prev => ({ ...prev, flagged: !prev.flagged }))}
                                        >
                                            <h4 className="font-semibold text-sm text-yellow-600">Suggested Flagged ({selectedTraderStaged.suggested_flagged?.length || 0})</h4>
                                            {collapsedSections.flagged ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                                        </Button>
                                        {!collapsedSections.flagged && (
                                            <div className="space-y-2 mt-2">
                                                {sortStagingPositions(selectedTraderStaged.suggested_flagged || []).filter(matchesFilters).map(staging => (
                                                    <PositionCard
                                                        key={staging.id}
                                                        position={staging.brokerage_account_data}
                                                        importDate={staging.import_date}
                                                        isStaged
                                                        suggestionType="skip"
                                                        selectionState={getSelectionState(staging.id)}
                                                        onSelectHedgeable={() => handleSelectHedgeable(staging.id)}
                                                        onSelectNonHedgeable={() => handleSelectNonHedgeable(staging.id)}
                                                        onSelectDismiss={() => handleSelectDismiss(staging.id)}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Suggested Non-Applicable */}
                                {(selectedTraderStaged.suggested_non_applicable?.length || 0) > 0 && (
                                    <div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="w-full flex items-center justify-between"
                                            onClick={() => setCollapsedSections(prev => ({ ...prev, nonApplicable: !prev.nonApplicable }))}
                                        >
                                            <h4 className="font-semibold text-sm text-orange-600">Suggested Non-Applicable ({selectedTraderStaged.suggested_non_applicable?.length || 0})</h4>
                                            {collapsedSections.nonApplicable ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                                        </Button>
                                        {!collapsedSections.nonApplicable && (
                                            <div className="space-y-2 mt-2">
                                                {sortStagingPositions(selectedTraderStaged.suggested_non_applicable || []).filter(matchesFilters).map(staging => (
                                                    <PositionCard
                                                        key={staging.id}
                                                        position={staging.brokerage_account_data}
                                                        importDate={staging.import_date}
                                                        isStaged
                                                        suggestionType="skip"
                                                        selectionState={getSelectionState(staging.id)}
                                                        onSelectHedgeable={() => handleSelectHedgeable(staging.id)}
                                                        onSelectNonHedgeable={() => handleSelectNonHedgeable(staging.id)}
                                                        onSelectDismiss={() => handleSelectDismiss(staging.id)}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* No staged positions message */}
                                {(selectedTraderStaged.suggested_accept_hedgeable?.length || 0) === 0 &&
                                 (selectedTraderStaged.suggested_accept_non_hedgeable?.length || 0) === 0 &&
                                 (selectedTraderStaged.suggested_flagged?.length || 0) === 0 &&
                                 (selectedTraderStaged.suggested_non_applicable?.length || 0) === 0 && (
                                    <div className="text-center text-muted-foreground py-8">
                                        No staged P&S positions found for this trader
                                    </div>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
